import { useMemo } from 'react';
import type { VoidFunctionComponent } from 'react';

import { window } from '../../../../../../globals';
import { useReportLogoSettings } from '../../../../../../hooks/api/organization/useReportLogoSettings';
import useReportOptions from '../../../../../../hooks/api/reports/useReportOptions';
import { selectOptionFactory } from '../../../../../../utils';
import { FormGroup, LabeledSelect } from '../../../../../generic/Forms';
import LogoUpload from '../../../../../LogoUpload';
import SettingsPageHeader from '../../../../../shared/settings/SettingsPageHeader';
import Paragraph from '../../../../../shared/settings/SettingsPageParagraph';
import Section from '../../../../../shared/settings/SettingsPageSection';
import { useReleaseGroupsSettings } from '../context';
import type { SelectOption } from '../../../../../../types';

const ReleaseGroupsSettingsReports: VoidFunctionComponent = () => {
  const {
    fileUrl,
    setFile,
    unsetFile,
    handleImgError,
    onSave: onSaveOrganizationLogo,
    isSaveDisabled: isOrganizationLogoSaveDisabled,
    isLoading: isOrganizationLogoLoading,
  } = useReportLogoSettings();

  const { data: settings, isDirty, isSaving, onUpdate, onSave: onSaveSettings } = useReleaseGroupsSettings();

  const organizationId = window.user?.organizationId ?? 0;
  const { data: reportOptionsData, isLoading } = useReportOptions(organizationId);

  const reportOptionSelectOptions = useMemo(
    () => reportOptionsData?.results.map((option) => selectOptionFactory(option.id, option.name)) ?? [],
    [reportOptionsData],
  );

  const selectedLicensingReport = useMemo(
    () =>
      settings.defaultSavedOptionLicensingReport
        ? (reportOptionSelectOptions.find((opt) => opt.value === settings.defaultSavedOptionLicensingReport) ?? null)
        : null,
    [settings.defaultSavedOptionLicensingReport, reportOptionSelectOptions],
  );

  const selectedSbom = useMemo(
    () =>
      settings.defaultSavedOptionSbom
        ? (reportOptionSelectOptions.find((opt) => opt.value === settings.defaultSavedOptionSbom) ?? null)
        : null,
    [settings.defaultSavedOptionSbom, reportOptionSelectOptions],
  );

  const isAnySaving = isOrganizationLogoLoading || isSaving;
  const isSaveButtonDisabled = isOrganizationLogoSaveDisabled && !isDirty;

  const onSaveAll = () => {
    if (!isOrganizationLogoSaveDisabled) {
      onSaveOrganizationLogo();
    }
    if (isDirty) {
      onSaveSettings();
    }
  };

  return (
    <>
      <SettingsPageHeader heading="Reports">
        <SettingsPageHeader.SaveButton onClick={onSaveAll} isDisabled={isSaveButtonDisabled} isLoading={isAnySaving} />
      </SettingsPageHeader>
      <Section>
        <Section.Left heading="Logo">
          <Section.Paragraph>
            Upload the logo that will appear on the cover and header of your reports. This is a global setting and
            will be applied to all projects and release groups in your organization.
          </Section.Paragraph>
        </Section.Left>
        <Section.Right>
          <LogoUpload fileUrl={fileUrl} setFile={setFile} unsetFile={unsetFile} handleImgError={handleImgError} />
        </Section.Right>
      </Section>
      <Section>
        <Section.Left heading="Saved Options">
          <Section.Paragraph>Choose default options for each report type.</Section.Paragraph>
        </Section.Left>
        <Section.Right>
          <FormGroup>
            <LabeledSelect<SelectOption<number>>
              label="Default options for Licensing reports"
              id="release-group-settings-default-saved-option-licensing-report"
              placeholder="No filter selected"
              onChange={(option) =>
                onUpdate({
                  defaultSavedOptionLicensingReport: option?.value ?? null,
                })}
              options={reportOptionSelectOptions}
              value={selectedLicensingReport}
              isDisabled={isLoading || isSaving}
              isClearable
              />
            <Paragraph>This filter will be active by default when a user visits the report</Paragraph>
            <LabeledSelect<SelectOption<number>>
              label="Default options for SBOM reports"
              id="release-group-settings-default-saved-option-sbom"
              placeholder="No filter selected"
              onChange={(option) =>
                onUpdate({
                  defaultSavedOptionSbom: option?.value ?? null,
                })}
              options={reportOptionSelectOptions}
              value={selectedSbom}
              isDisabled={isLoading || isSaving}
              isClearable
              />
            <Paragraph>This filter will be active by default when a user visits the report</Paragraph>
          </FormGroup>
        </Section.Right>
      </Section>
    </>
  );
};

export default ReleaseGroupsSettingsReports;
